import React, { useState, useRef, useEffect } from 'react';
import { Card, Row, Col, Spin, Typography, Tooltip, Button, Space, Select, Tag } from 'antd';
import {
  BorderOutlined,
  AimOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import MultiPaneChart, { MultiPaneChartRef } from '../Chart/MultiPaneChart';
import { useYahooFinanceChartData } from '../../hooks/useYahooFinanceChartData';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
import RightSidebarPanel from '../RightSidebarPanel';
import AnalysisTabs from '../Analysis/AnalysisTabs';
import { getMarketStatus, formatMarketHours } from '../../utils/marketHours';
import './StockDashboard.css';
import { logger } from '../../utils/logger';
import { INDICATOR_METADATA, IndicatorId } from '../../constants/indicators';

const { Text } = Typography;

interface StockDashboardProps {
    onSaveSignal: (analysisResult: any, context: any) => void;
    selectedIndicators: IndicatorId[];
    onIndicatorToggle: (indicatorId: IndicatorId) => void;
    onOpenIndicatorPanel: () => void;
    indicatorPlacements: Record<IndicatorId, 'main' | 'pane'>;
    onPlacementChange: (indicatorId: IndicatorId, placement: 'main' | 'pane') => void;
}

const StockDashboard: React.FC<StockDashboardProps> = ({
    onSaveSignal,
    selectedIndicators,
    onIndicatorToggle,
    onOpenIndicatorPanel,
    indicatorPlacements,
    onPlacementChange
}) => {
    const { theme } = useAppTheme();
    const { t } = useTranslation();
    const [selectedStock, setSelectedStock] = useState('AAPL');
    const [selectedInterval, setSelectedInterval] = useState('1h');
    const [screenWidth, setScreenWidth] = useState<number>(() => window.innerWidth);
    const [isRectangleMode, setIsRectangleMode] = useState(false);
    const [isPointMode, setIsPointMode] = useState(false);

    // Responsive breakpoints (same as App.tsx)
    const isLargeScreen = screenWidth >= 1024;
    const isTabletScreen = screenWidth >= 768;

    // Listen for screen size changes
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update market status every minute
    useEffect(() => {
        const updateMarketStatus = () => {
            setMarketStatus(getMarketStatus());
        };

        // Update immediately
        updateMarketStatus();

        // Update every minute
        const interval = setInterval(updateMarketStatus, 60000);

        return () => clearInterval(interval);
    }, []);

    // Chart tool states
    const [selectedRect, setSelectedRect] = useState<any>(null);
    const [selectedPoints, setSelectedPoints] = useState<{ time: number; price: number }[]>([]);
    const chartApiRef = useRef<MultiPaneChartRef | null>(null);

    // Market status state
    const [marketStatus, setMarketStatus] = useState(getMarketStatus());

  const {
    data: stockData,
    error,
    isLoading,
    isConnected,
    latestPrice,
    priceChange,
    updateSubscription,
    lastUpdateType,
    lastUpdatedCandle
  } = useYahooFinanceChartData({
    symbol: selectedStock,
    interval: selectedInterval,
    autoStart: true
  });


    // Handle symbol and interval changes
    const handleSymbolChange = (newSymbol: string) => {
        setSelectedStock(newSymbol);
        if (updateSubscription) {
            updateSubscription(newSymbol, selectedInterval);
        }
    };

    const handleIntervalChange = (newInterval: string) => {
        setSelectedInterval(newInterval);
        if (updateSubscription) {
            updateSubscription(selectedStock, newInterval);
        }
    };

    // Handle screen resize
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle signal selection
    const handleSaveSignalLocal = (analysisResult: any, context: any) => {
        logger.log('Signal saved:', analysisResult, context);
        // 传递当前选中的股票代码和时间间隔
        const contextWithSymbol = {
            ...context,
            symbol: selectedStock,  // 传递当前选中的股票代码（如AAPL）
            timeframe: selectedInterval  // 传递当前选中的时间间隔（如1h）
        };
        onSaveSignal(analysisResult, contextWithSymbol);
    };

    return (
        <div
            className="stock-dashboard"
            style={{
                background: theme.appBackground,
                color: theme.textPrimary,
                minHeight: 'auto'
            }}
        >
            {/* Main Content Area - Similar to Crypto Dashboard */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: '16px'
            }}>
                {/* Top Row: Chart and Signal Analysis Assistant */}
                <div style={{
                    display: 'flex',
                    flexDirection: isLargeScreen ? 'row' : 'column',  // 大屏幕水平布局，小屏幕垂直
                    gap: isLargeScreen ? '16px' : '8px',
                    minHeight: 'auto',
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    {/* Chart Area */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Card
                            title={
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                        <Space align="center" size="middle" wrap>
                                            <Select
                                                value={selectedStock}
                                                onChange={handleSymbolChange}
                                                style={{ width: 200 }}
                                                size="large"
                                                dropdownMatchSelectWidth={false}
                                                options={[
                                                    { value: 'AAPL', label: 'AAPL' },
                                                    { value: 'GOOGL', label: 'GOOGL' },
                                                    { value: 'MSFT', label: 'MSFT' },
                                                    { value: 'TSLA', label: 'TSLA' },
                                                    { value: 'NVDA', label: 'NVDA' },
                                                    { value: 'AMZN', label: 'AMZN' },
                                                    { value: 'META', label: 'META' },
                                                    { value: 'GC=F', label: 'GC=F (Gold Dec 25)' },
                                                    { value: '.IXIC', label: '.IXIC (NASDAQ)' },
                                                    // ETFs
                                                    { value: 'SPY', label: 'SPY (S&P 500)' },
                                                    { value: 'VOO', label: 'VOO (S&P 500)' },
                                                    { value: 'QQQ', label: 'QQQ (Nasdaq-100)' },
                                                    { value: 'VTI', label: 'VTI (Total US)' },
                                                    { value: 'DIA', label: 'DIA (Dow Jones)' },
                                                    { value: 'IWM', label: 'IWM (Russell 2000)' },
                                                    { value: 'XLF', label: 'XLF (Financials)' },
                                                    { value: 'XLV', label: 'XLV (Healthcare)' },
                                                    { value: 'SMH', label: 'SMH (Semis)' },
                                                    { value: 'XLE', label: 'XLE (Energy)' }
                                                ]}
                                            />
                                            <span style={{ color: theme.textSecondary, fontWeight: 600 }}>@</span>
                                            <Select
                                                value={selectedInterval}
                                                onChange={handleIntervalChange}
                                                style={{ width: 100 }}
                                                size="large"
                                                options={[
                                                    { value: '1m', label: '1m' },
                                                    { value: '5m', label: '5m' },
                                                    { value: '15m', label: '15m' },
                                                    { value: '30m', label: '30m' },
                                                    { value: '1h', label: '1h' },
                                                    { value: '1d', label: '1d' }
                                                ]}
                                            />
                                            <Button
                                                icon={<BarChartOutlined />}
                                                onClick={onOpenIndicatorPanel}
                                                size="large"
                                            >
                                                {t('indicator.button')}
                                            </Button>
                                        </Space>
                                        <Space align="center" size="middle" wrap style={{ justifyContent: 'flex-end' }}>
                                            <Text style={{ color: theme.textSecondary }}>
                                                {t('stock.status')}: <span style={{ color: isConnected ? '#52c41a' : '#ff4d4f' }}>
                                                    {isConnected ? t('realtime.connected') : t('realtime.disconnected')}
                                                </span>
                                            </Text>
                                            {latestPrice && (
                                                <div>
                                                    <Text style={{ color: theme.textSecondary, marginRight: 8 }}>{t('stock.price')}:</Text>
                                                    <Text style={{
                                                        color: priceChange.isPositive ? '#52c41a' : '#ff4d4f',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        ${latestPrice.toFixed(2)}
                                                    </Text>
                                                    <Text style={{
                                                        color: priceChange.isPositive ? '#52c41a' : '#ff4d4f',
                                                        fontSize: '12px',
                                                        marginLeft: '8px'
                                                    }}>
                                                        {priceChange.isPositive ? '+' : '-'}${priceChange.value.toFixed(2)} ({priceChange.percentage.toFixed(2)}%)
                                                    </Text>
                                                </div>
                                            )}
                                            <Tag
                                                color={marketStatus.isOpen ? 'green' : 'red'}
                                                icon={marketStatus.isOpen ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                            >
                                                {marketStatus.isOpen ? t('market.open') : t('market.closed')}
                                            </Tag>
                                        </Space>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Text style={{ color: theme.textSecondary, fontSize: '12px' }}>
                                            {t('market.hoursNotice')}
                                        </Text>
                                        <Tooltip title={formatMarketHours()}>
                                            <Text style={{ color: theme.textSecondary, fontSize: '12px' }}>
                                                {formatMarketHours()}
                                            </Text>
                                        </Tooltip>
                                    </div>
                                </div>
                            }
                            style={{
                                background: theme.surfaceBackground,
                                borderColor: theme.surfaceBorder,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                            bodyStyle={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                padding: 0
                            }}
                        >
                            {isLoading ? (
                                <div className="chart-loading" style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                    <Text style={{ color: theme.textPrimary, marginTop: '16px', display: 'block' }}>
                                        Loading stock data...
                                    </Text>
                                </div>
                            ) : stockData.length > 0 ? (
                                <div style={{ display: 'flex', gap: '8px', width: '100%', height: '100%' }}>
                                    {/* 股票图表工具栏侧边栏 */}
                                    <div style={{
                                        width: '55px',
                                        background: theme.surfaceBackground,
                                        border: `1px solid ${theme.surfaceBorder}`,
                                        borderRadius: '8px',
                                        padding: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                        height: 'auto',
                                        minHeight: '500px',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: theme.textPrimary,
                                            marginBottom: '8px',
                                            textAlign: 'center'
                                        }}>
                                            Tools
                                        </div>

                                        <Tooltip title="Rectangle Tool">
                                            <Button
                                                type={isRectangleMode ? 'primary' : 'default'}
                                                icon={<BorderOutlined />}
                                                size="small"
                                                onClick={() => {
                                                    chartApiRef.current?.toggleRectangleTool();
                                                    setIsRectangleMode(prev => {
                                                        const next = !prev;
                                                        if (next) setIsPointMode(false);
                                                        return next;
                                                    });
                                                }}
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            />
                                        </Tooltip>

                                        <Tooltip title="Point Tool">
                                            <Button
                                                type={isPointMode ? 'primary' : 'default'}
                                                icon={<AimOutlined />}
                                                size="small"
                                                onClick={() => {
                                                    chartApiRef.current?.togglePointTool();
                                                    setIsPointMode(prev => {
                                                        const next = !prev;
                                                        if (next) setIsRectangleMode(false);
                                                        return next;
                                                    });
                                                }}
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            />
                                        </Tooltip>

                                        {selectedRect && (
                                            <Tooltip title="Clear Rectangle">
                                                <Button
                                                    icon={<ClearOutlined />}
                                                    size="small"
                                                    onClick={() => {
                                                        chartApiRef.current?.clearRectangle();
                                                        setSelectedRect(null);
                                                    }}
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                />
                                            </Tooltip>
                                        )}

                                        {selectedPoints.length > 0 && (
                                            <Tooltip title="Clear Points">
                                                <Button
                                                    icon={<ClearOutlined />}
                                                    size="small"
                                                    onClick={() => {
                                                        chartApiRef.current?.clearPoints();
                                                        setSelectedPoints([]);
                                                    }}
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                />
                                            </Tooltip>
                                        )}

                                    </div>

                                    {/* 股票图表区域 */}
                                    <div style={{ flex: 1 }}>
                                        <MultiPaneChart
                                            ref={chartApiRef}
                                            ohlcData={stockData}
                                            onRectChange={(rect) => {
                                                setSelectedRect(rect);
                                                if (rect) {
                                                    setIsRectangleMode(true);
                                                    setIsPointMode(false);
                                                }
                                            }}
                                            onPointsChange={setSelectedPoints}
                                            mainHeight={isLargeScreen ? 700 : 500}  // Match crypto chart height
                                            subHeight={isLargeScreen ? 180 : 150}   // Match crypto chart sub height
                                            width={0}
                                            hideToolbar={true}
                                            lastUpdateType={lastUpdateType}
                                            lastUpdatedCandle={lastUpdatedCandle}
                                            showVolume={selectedIndicators.includes('volume')}
                                            showRsi={selectedIndicators.includes('rsi')}
                                            showMacd={selectedIndicators.includes('macd')}
                                            showSma={selectedIndicators.includes('sma')}
                                            showEma={selectedIndicators.includes('ema')}
                                            showWma={selectedIndicators.includes('wma')}
                                            showWema={selectedIndicators.includes('wema')}
                                            showTypicalPrice={selectedIndicators.includes('typicalPrice')}
                                            showObv={selectedIndicators.includes('obv')}
                                            showBollingerBands={selectedIndicators.includes('bollingerBands')}
                                            showWilliamsR={selectedIndicators.includes('williamsR')}
                                            showAtr={selectedIndicators.includes('atr')}
                                            showMfi={selectedIndicators.includes('mfi')}
                                            showStochastic={selectedIndicators.includes('stochastic')}
                                            showRoc={selectedIndicators.includes('roc')}
                                            showTrix={selectedIndicators.includes('trix')}
                                            showVwap={selectedIndicators.includes('vwap')}
                                            showAdl={selectedIndicators.includes('adl')}
                                            showAdx={selectedIndicators.includes('adx')}
                                            showAo={selectedIndicators.includes('ao')}
                                            showCci={selectedIndicators.includes('cci')}
                                            onIndicatorToggle={onIndicatorToggle}
                                            indicatorPlacements={indicatorPlacements}
                                            onPlacementChange={onPlacementChange}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSecondary }}>
                                    No stock data available
                                </div>
                            )}
                        </Card>
                    </div>

                    {/* Signal Analysis Assistant - Right Sidebar */}
                    {isTabletScreen && (  // 只在平板及以上显示
                        <div style={{
                            width: isLargeScreen ? 'min(400px, 35vw)' : '100%',
                            minWidth: isLargeScreen ? '320px' : 'auto',
                            height: isLargeScreen ? '770px' : '400px',
                            flexShrink: 0,
                            boxSizing: 'border-box'
                        }}>
                            <RightSidebarPanel
                                onSignalSelect={(signal) => {
                                    logger.log('Selected signal:', signal);
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Bottom Row: Analysis Tabs */}
                {!isLoading && (
                    <div style={{ width: '100%' }}>
                        <AnalysisTabs
                            ohlcData={stockData}
                            selectedRectangle={selectedRect}
                            selectedPoint={selectedPoints && selectedPoints.length > 0 ? selectedPoints[0] : undefined}
                            chartApiRef={chartApiRef}
                            onSaveSignal={handleSaveSignalLocal}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default StockDashboard;
